import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Database } from 'lucide-react';
import { haltGate, stopRuns } from './runHalt';
import { DimensionsInfo } from './DimensionsInfo';
import { ListingCropsInfo } from './ListingCropsInfo';
import { HardwareCropsInfo } from './HardwareCropsInfo';
import { PartsListInfo } from './PartsListInfo';
import { SchemaBoxesInfo } from './SchemaBoxesInfo';
import { MasksInfo } from './MasksInfo';
import { EstimateInfo } from './EstimateInfo';
import { usePartCategories } from './partCategories';
import { partKindOf, type ModelsSub } from './modelsStages';
import { useRunsInFlight } from './generatorMemory';
import { PartSelector, type SelectorPart } from './PartSelector';
import { CenteredSelect } from './CenteredSelect';
import { PendingPhase } from './PendingPhase';
import { ProductModelStep } from './ProductModelStep';
import { PhaseControls } from './PhaseControls';
import { ChainBadge } from './ChainBadge';
import { RealImagesStep } from './RealImagesStep';
import { ThreeDStep } from './ThreeDStep';
import { assemblyStepsFrom, useCategoryBands } from './categoryBands';
import { getRun } from './categoryRun';
import { runPageCrops } from './hardwareCropsRun';
import { useImagesEpoch } from './imagesRefresh';
import { getColourways, useListing } from './listingStore';
import { RUN_MODEL } from './models';
import { usePartIdentities } from './partIdentity';
import { usePartModels } from './partModels';
import { useArrowSteps } from './stepThrough';
import {
  PART_GROUPS, extractParts, partGenKey, partSearchKey,
  usePartsRun,
} from './partsRun';
import { useCategories } from './catalogCategories';
import { shownId } from './partsCatalogue';
import { useCatalogItems } from './catalogItems';
import { HardwareSetStep } from './HardwareSetStep';
import { HardwareTable } from './HardwareTable';
import { setHardwareTab } from './hardwareTab';
import { HardwareAxisTable } from './HardwareAxisTable';
import { HolesTable } from './HolesTable';
import { useHardwareInteractions, type HardwareInteraction } from './hardwareInteractionsRun';
import { setName, setsOf, useKeptSetKeys, type HardwareSet } from './hardwareSets';
import { useToolDriven, useToolSets, type ToolSet } from './toolSet';
import { invalidatePartModels } from './partModels';
import { fillCatalogSizes } from './catalogItems';
import { runDimensions, runDimensionsIfUseful } from './dimensionsRun';
import { callError } from './callError';
import { runListingCrops } from './listingCropsRun';
import { renderPartCrops } from './partCrop';
import { getParts } from './partsRun';
import { readPartCategories } from './partCategories';
import { generatedUnder, loadGenerated } from './realImages';
import { useSubmodelFilter } from './submodelFilterStore';
import { usePlace } from './lastPlace';
import { useLanguage } from '../../contexts/LanguageContext';
import { clock, useWaited } from './waitClock';

const CATEGORY = 'Matériel fourni';

// The selector's first segment: every part at once, rather than one of them. Not a part's
// key and unable to collide with one — those are a reference, a printed name, or "#3".
const ALL_KEY = '\x1fall';
// ONE KIND OF PART AT ONCE — "all parts" narrowed to the components, the hardware or the tools.
const KIND_KEY = (kind: (typeof PART_GROUPS)[number]) => `\x1fkind:${kind}`;
// AND THE WHOLE PRODUCT, which is the mesh phase's first segment and no part at all — see
// ProductModelStep. Same \x1f, for the same reason: a part's key is a reference, a printed
// name, or "#3", and none of those can collide with this.
// AND A HARDWARE SET'S, by the set's own key — see hardwareSets. \x1f cannot occur in a part's.
const SET_KEY = (key: string) => `\x1fset:${key}`;
// AND HARDWARE & COVERS' TWO TABLES, as tabs of the strip ahead of the sets: the hardware table
// (what works together, its holes) and how each piece of hardware goes in.
const HW_TABLE_KEY = '\x1fhw:table';
const HW_AXIS_KEY = '\x1fhw:axis';
// AND THE ONE TAB EVERY MODEL OF IT IS UNDER, picked in the card's dropdown (see modelPicker).
const HW_MODELS_KEY = '\x1fhw:models';

// THE CALLS ONE PRESS OF THIS PHASE MAKES, in the order it makes them. The button counts
// itself through this list, so adding one is a line here and nothing else — and the "n/3" on
// screen cannot disagree with what is running. What each part is made of was a fourth, and is
// Phase 5's reading now.
const STAGES = ['list', 'sizes', 'crops', 'manual'] as const;
const STAGE_KEYS: Record<number, string> = {
  0: 'notice.parts.stageList',
  1: 'notice.parts.stageSizes',
  2: 'notice.parts.stageCrops',
  3: 'notice.parts.stageManual',
};

// Phase 2 — the PARTS, one part at a time, in every way the pipeline knows it.
//
// Three phases once, then two views, now one page. The same list of parts in all of them:
// what the manual says the box contains, what each of those pieces actually looks like,
// and what it is as a mesh. Splitting them said they were stages of a pipeline, which they
// are; it also meant a part with no photograph sat in one place while the part it is sat
// in another, and answering "what have we got for this piece" was reading two screens and
// lining them up by eye.
//
// So the SELECTION is the phase's shape: pick a part, and everything known about it is
// under it in the order the work happens — what it is, what the manual draws, what was
// drawn from that, and what was reconstructed from those. See PartSelector.
//
// WHAT DOES NOT CHANGE is what runs. The parts list is one Gemini call over the manual;
// every image and every reconstruction is a paid, per-part decision, taken here by hand.
// Putting them on one page is not an invitation to run them together, and the run-all
// chain still stops at the list — see runAll.
export function PartsPhase({ file, docId, locked, held, models = false, view = 'parts', sub }: {
  file: Blob;
  docId: string;
  locked?: boolean;
  // The run that holds this phase is paused: it is not coming until Reprendre.
  held?: boolean;
  // The 3D pipeline: photographs are asked for one angle at a time, and each part carries
  // a reconstruction under its table. The video pipeline has neither.
  models?: boolean;
  // WHICH HALF OF ONE PART THIS PHASE SHOWS — the same page, cut in two.
  //
  // 'parts' is what the manual says and what was drawn from it: the extraction, the table,
  // the photographs. 'meshes' is the reconstruction made out of those, which is now a phase
  // of its own (see viewerPipelines, where the reason is written: holes are decided in
  // Phase 4, and a mesh built in Phase 2 was built before anything knew about them).
  //
  // ONE COMPONENT FOR BOTH, and not two, because everything around the part is shared: which
  // part is open, the submodel filter, the segmented selector, the sub-step outputs that can
  // also be opened in it. Two copies of that would be two selectors that disagree about
  // which part you are looking at.
  view?: 'parts' | 'meshes';
  // IN THE MESH PHASE, WHICH OF ITS THREE SUBPHASES (see ModelsPhase): the whole product alone,
  // or the strip cut down to the hardware or to the component parts. Absent is all of it.
  sub?: ModelsSub;
}) {
  const { t } = useLanguage();
  const bands = useCategoryBands(docId, CATEGORY);
  const { busy, progress, items, error } = usePartsRun(docId);
  // For the product name the size read sends as context.
  const listing = useListing(docId);
  // Only worth showing when there's more than one unit of work — "0/1" says nothing.
  const progressSuffix = busy && progress && progress.total > 1 ? ` ${progress.done}/${progress.total}` : '';

  // Listing the parts and sizing them are one action: the sizes are read off the
  // retailer's dimension drawing for the very list this call produces, and there is no
  // moment at which you want one without the other. The size half is silent when there is
  // nothing to read — no drawing in the strip, or every part already sized — so it costs
  // nothing on the manuals that have no dimensions anywhere.
  //
  // The button spins for the WHOLE action, not just its first half. The parts store's
  // `busy` covers the extraction; the size read that follows is a call of its own and used
  // to happen after the button had gone quiet, so the phase looked finished while it was
  // still working — and the table appearing under a settled button made that read as done.
  // WHICH OF THE FOUR CALLS THIS PRESS IS ON, or null when nothing is running.
  //
  // One press makes four: the list, the sizes read off the dimension drawing, the search for
  // each part in the listing photographs, and what each part is made of. It used to be two
  // booleans — one for the size read, one for "the tail" — which between them said only that
  // something was happening. A minute of spinner with no way to tell a listing search halfway
  // through from one that has hung is the thing this replaces.
  //
  // A position in STAGES rather than a name, because the button prints "3/4" and the count has
  // to come from the same place the word does.
  const [stage, setStage] = useState<number | null>(null);
  // WHAT THIS PRESS STILL OWES, so the rows it is going to overwrite stop showing the last
  // run's answer as though it were settled.
  //
  // Every one of these rows already waits while its OWN call is out. What it could not see is
  // the window BEFORE that call starts, which for the last two is minutes: at "2/4 sizes" the
  // material picks and the listing crops on screen are the previous run's, they look exactly
  // like an answer, and the press is working towards erasing them.
  //
  // "At or before this stage" and not "at it": a stage that has not been reached owes its
  // answer just as much as the one running. Undefined when nothing is running, which is what
  // tells a cell it is looking at a finished answer.
  const owes = (name: typeof STAGES[number]) =>
    stage !== null && stage <= STAGES.indexOf(name);
  // THE SIZE READ ON ITS OWN, from its badge (see DimensionsInfo) — a stage of the press made
  // without the other three. Here and not in the badge, because what it owes is this phase's:
  // the table's size cells wait on it exactly as they wait on the press's own stage 2.
  const [sizingAlone, setSizingAlone] = useState(false);
  const [sizeError, setSizeError] = useState('');
  const sizing = owes('sizes') || sizingAlone;
  // The whole press, from the first call to the last. Every one of the four has its busy flag
  // in its own store and this button can see only the extraction's, so a press that finished
  // one call re-enabled the button on top of the next.
  const running = busy || stage !== null;
  // THE LIST THIS DOCUMENT HAS, and not the list it has THIS INSTANT. A fresh extraction
  // empties `items` before it fills them in page by page (see extractParts, which does it so
  // the previous run's answer is never mistaken for this one's) — so for the whole of the
  // call that is about to produce a list, `items.length` is 0.
  //
  // Everything that asks "is there a list" and is drawn NEXT TO THE BUTTON has to ask it of
  // the document rather than of the instant: three of the four badges vanished from under
  // the pointer on the one press that makes them worth reading, and the button renamed itself
  // mid-run from "List all parts" back to "List the supplied parts".
  //
  // A ref and not state: it only ever goes from false to true, and the render that would set
  // it is the render where `items` arrived. Keyed by document, because the next manual has
  // not listed anything.
  const listed = useRef({ doc: docId, ever: false });
  if (listed.current.doc !== docId) listed.current = { doc: docId, ever: false };
  listed.current.ever = listed.current.ever || items.length > 0;
  const hasList = items.length > 0 || listed.current.ever;
  // And the list those badges DESCRIBE: the live one, or the last one this document had. A
  // prompt built from an empty list mid-re-run would be a preview of a call about nothing.
  const listedItems = useRef(items);
  if (items.length) listedItems.current = items;
  // HOW LONG THIS PRESS HAS BEEN GOING. The page counter says how much of the manual has
  // been read and says nothing when there is only one page to read; the clock says whether
  // a wait is nearly over, which is the question a stalled-looking spinner raises.
  //
  // Over both halves of what the button launches — the list and the size read that follows
  // it — because that is one press and one wait. Same ticker as the other buttons in the
  // app that go and ask a model something (waitClock).
  const waited = useWaited(running);

  // The button's SCOPE is the selector's. On "All parts" it means the phase; on one part
  // it means that part, and nothing else in the list moves — see reReadOnePart for why
  // that is worth having when the call costs the same either way.
  //
  // The size read only ever runs on the whole press. It answers by index into the list it
  // is sent, so there is no reading one part's dimensions; and a scoped press that quietly
  // rewrote every other part's axes would be exactly what the scope is there to prevent.
  const listAndSize = async () => {
    if (!showAll && !showKind && items.length > 0) {
      await extractParts(docId, file, bands, t, false, items[open]);
      return;
    }
    setStage(STAGES.indexOf('list'));
    // STOP, from the badge: each stage asks before it starts, and the two long ones (the list,
    // the crops) between their own calls too. See runHalt.
    const halted = haltGate(docId);
    try {
      await extractParts(docId, file, bands, t);
      if (halted()) return;
      setStage(STAGES.indexOf('sizes'));
      // Forced: pressing this button is asking for the phase again, and the sizes are
      // part of it. The chain keeps the cheap skip.
      await runDimensionsIfUseful(docId, listing.productName, RUN_MODEL, true);
      // AND THE REST OF THE PHASE: find each part in the listing photographs — which the chain
      // leaves out, being the longest wait in the app. Which crops to keep is a rule over them
      // (see listingSelectRun), not a call.
      const parts = getParts(docId);
      if (parts.length && !halted()) {
        setStage(STAGES.indexOf('crops'));
        const cats = await readPartCategories(parts);
        const drawn = await renderPartCrops(file, parts);
        // EVERY COLOURWAY, one after another: Phase 3 reads them all at once, and a colourway
        // with no close-ups gets no texture.
        const subs = getColourways(docId).map((s) => s.id);
        for (const sub of subs.length ? subs : [undefined]) {
          if (halted()) return;
          await runListingCrops(docId, parts, cats, drawn, undefined, sub);
        }
        // AND WHERE EACH IS DRAWN IN THE STEPS. Run all runs it again at its very end, once the
        // step analysis has added the tools, which a press here leaves without drawings.
        const { steps } = assemblyStepsFrom(getRun(docId).segments);
        if (steps.length && !halted()) {
          setStage(STAGES.indexOf('manual'));
          await runPageCrops(docId, file, steps, parts, drawn);
        }
      }
    } finally {
      setStage(null);
    }
  };

  // Every part and the product, as the press's stage 2 reads them — but a direct instruction,
  // so a failure is said out loud rather than swallowed the way the chain swallows it.
  const sizeAlone = async () => {
    setSizingAlone(true);
    setSizeError('');
    try {
      await runDimensions(docId, listing.productName, RUN_MODEL);
    } catch (e) {
      setSizeError(callError(e, t));
    } finally {
      setSizingAlone(false);
    }
  };

  // Which catalog row each part is and what it is called — the selector's labels, and what
  // the 3D card hangs its photographs and meshes off.
  const identities = usePartIdentities(items);
  // The database's category for each part, which is what the calls beside the button send.
  const categories = usePartCategories(listedItems.current);

  // Hand the database any size it is missing for a part it already holds. See
  // fillCatalogSizes: the size is read here and the row is shared, and the save that
  // would have carried it across only ever happens once per part.
  useEffect(() => {
    if (items.length) void fillCatalogSizes(items);
  }, [items]);

  // WHAT EVERY PART'S 3D MODEL MEASURES is kept by ModelSizeSync, mounted with the manual.
  const { items: catalog, loaded: catalogLoaded } = useCatalogItems();
  // The category ontology: which kind each part is below.
  const ontology = useCategories();
  const submodelFilter = useSubmodelFilter(docId);
  const imagesEpoch = useImagesEpoch(docId);
  // Which parts have a model in the Parts DB, and which have been drawn — the two things
  // the selector's mark can mean, one per pipeline.
  const partModels = usePartModels();
  const generated = useMemo(
    () => loadGenerated(docId),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [docId, imagesEpoch],
  );

  // Which part is open. Pinned by KEY rather than by index so it survives the list being
  // rebuilt, and falling back to the first: there is always a part on screen, and a phase
  // that opened on nothing would ask you to pick before it showed you anything to pick by.
  //
  // REMEMBERED across a refresh, per manual — a part is where you were working, and this
  // phase is a long one. A key from a parts list that has since been read again is simply
  // not found, which is the same fallback as never having chosen: the first part.
  const [openKey, setOpenKey] = usePlace(docId, 'part');
  // ALL PARTS: not a part, a way of looking at them. The table already draws every part as
  // a column — that is what it did before a selector existed — so this is the phase's one
  // view where a manual can be read across rather than down: every description under every
  // other, every size against every other.
  //
  // \x1f cannot occur in a part's key: those are a reference, a printed name, or "#3".
  // AND NOT IN THE MESH PHASE, which has no such view. "All parts" is a SCOPE: the table drops
  // its filter and draws every part as a column, and the run button becomes the whole chain over
  // all of them. Phase 5 scopes nothing — it draws one part's reconstruction — so the segment
  // led to a page that said "pick a part above", which is a tile whose only function is to show
  // nothing. It is gone from the strip there (see `segments`).
  //
  // FALSE HERE AS WELL AS ABSENT THERE, because the open segment is remembered per MANUAL and
  // the two phases share the memory: "all parts" chosen in Phase 2 arrives here as a key the
  // mesh strip no longer has. Read as false it falls through to the first part, exactly as the
  // product key does on the way back — the same stored key serving both phases, and each phase
  // simply not finding the other's.
  const showAll = view !== 'meshes' && openKey === ALL_KEY;
  // OR ONE KIND OF THEM: "all parts" with only that kind's columns (see kindCols).
  const showKind = view === 'meshes' ? null : PART_GROUPS.find((k) => openKey === KIND_KEY(k)) ?? null;
  // THE WHOLE PRODUCT, and only in the phase that makes meshes. Phase 2 draws what the manual
  // prints about each supplied part, and the assembled chair is not one of them — the same
  // stored key serves both phases, so a product chosen here must simply not be found there.
  const showProduct = view === 'meshes' && sub === 'product';
  // THE HARDWARE BUILT TOGETHER, one segment each — Phase 4's hardware table, as sets (see
  // setsOf). Ahead of the parts in the hardware subphase: a part of a set gets its model from
  // the set, so the set is the first thing to do — and its parts are reached from its card.
  //
  // AND IN THE TOOLS SUBPHASE, EACH TOOL WITH THE HARDWARE IT DRIVES (see toolSet) — the same
  // dropdown, with no table row behind it.
  const hardwareTable = useHardwareInteractions(docId);
  const interactions = hardwareTable.interactions;
  const toolSets = useToolSets(docId);
  const sets = useMemo<{ set: HardwareSet | ToolSet; one?: HardwareInteraction }[]>(
    () => (view !== 'meshes' ? []
      : sub === 'hardware' ? setsOf(interactions, items, catalog, catalogLoaded)
        : sub === 'tools' ? toolSets.map((set) => ({ set })) : []),
    [view, sub, interactions, items, catalog, catalogLoaded, toolSets],
  );
  const openSet = sets.find(({ set }) => SET_KEY(set.key) === openKey) ?? null;
  // A part with no reference and no name still needs an identity here; its position is the
  // only thing left that tells it from the others.
  const keys = useMemo(
    () => items.map((p, i) => partSearchKey(p) || `#${i}`),
    [items],
  );
  // WHICH PARTS THIS SUBPHASE IS ABOUT: its kind, by the one rule the progress reads too (see
  // partKindOf). The stored key is shared with the other subphases and with Phase 2, so a part
  // of another kind simply is not found here, and the first of this kind is opened instead.
  // THE COVERS ARE HARDWARE, and their models are made with the rest of it — a set's cover is
  // built with the set. Where they go is the Covers placement subphase's.
  const kindWanted = sub === 'hardware' ? 'Hardware' : sub === 'components' ? 'Components'
    : sub === 'tools' ? 'Tools' : null;
  const inSub = (i: number) => !kindWanted || partKindOf(items[i], categories[i], ontology) === kindWanted;
  // THE PARTS OF ONE KIND, by id — the columns of its tab (see showKind).
  const kindCols = (kind: (typeof PART_GROUPS)[number]) =>
    items.flatMap((p, i) => (partKindOf(p, categories[i], ontology) === kind ? [i] : []));
  const found = openKey ? keys.indexOf(openKey) : -1;
  // THE PARTS OF A SET ARE NOT TILES OF THEIR OWN: they are reached from the set's card, by its
  // dropdown (see modelPicker).
  const inASet = useMemo(
    () => new Set(sets.flatMap(({ set }) => set.parts.map((p) => p.at))),
    [sets],
  );
  // AND THE HARDWARE A TOOL DRIVES IS NOT HARDWARE & COVERS' TO LIST, where no set of it places it:
  // its model is made with its tool (see toolSet), and its card here could only say so.
  const toolDriven = useToolDriven(docId);
  const madeWithTool = (i: number) => sub === 'hardware' && toolDriven.has(i) && !inASet.has(i);
  const firstInSub = items.findIndex((_, i) => inSub(i) && !inASet.has(i) && !madeWithTool(i));
  // A PART OF A SET, ARRIVING FROM ANOTHER PHASE: its set is what opens. A set's part has no card
  // of its own — the set's viewer shows one part alone by hiding the others.
  const memberSet = !openSet && found >= 0
    ? sets.find(({ set }) => set.parts.some((p) => p.at === found)) ?? null : null;
  const open = !memberSet && found >= 0 && inSub(found) && !madeWithTool(found) ? found : Math.max(0, firstInSub);
  // THE SET ON SCREEN: the one opened, the one a part open is in, or — where every piece of
  // hardware is in a set — the first.
  // ONE OF HARDWARE & COVERS' TABLES, open in place of a model.
  const hwView = view === 'meshes' && sub === 'hardware' && (openKey === HW_TABLE_KEY || openKey === HW_AXIS_KEY)
    ? openKey : null;
  const shownSet = hwView ? null : openSet ?? memberSet ?? (firstInSub < 0 ? sets[0] ?? null : null);
  // THE MODEL THE GENERATION BADGE IS ABOUT WHILE A TABLE IS OPEN: the set or part last opened, or
  // the first of them. The badge stays in the phase's row whatever the strip shows (see badgeOnly).
  const lastModel = useRef<string | null>(null);
  if (!hwView && openKey) lastModel.current = openKey;
  const genSet = hwView ? sets.find(({ set }) => SET_KEY(set.key) === lastModel.current) ?? null : null;
  const genPart = (() => {
    if (!hwView || genSet) return -1;
    const at = lastModel.current ? keys.indexOf(lastModel.current) : -1;
    if (at >= 0 && inSub(at)) return at;
    return firstInSub;
  })();
  const genFirstSet = hwView && !genSet && genPart < 0 ? sets[0] ?? null : null;
  // ?item=<catalog id>: arrive with one part already open. What the Parts DB's name column
  // links back with — the reverse of this table's own "FOUND IN DATABASE" link, so the two
  // pages point at each other.
  //
  // By CATALOG ID and not by name: the database's name and the manual's are the same
  // string only when the model matched them, which is the case this link exists for but
  // not the only case it will meet. Applied once, so selecting another part afterwards
  // sticks rather than being pulled back by a parameter still in the address bar.
  const [search] = useSearchParams();
  const wantedItem = Number(search.get('item')) || null;
  const arrived = useRef(false);
  useEffect(() => {
    if (arrived.current || !wantedItem || !items.length) return;
    const at = identities.findIndex((identity) => identity?.itemId === wantedItem);
    if (at < 0) return;
    arrived.current = true;
    setOpenKey(keys[at]);
  }, [wantedItem, items.length, identities, keys]);


  // The 3D builds out now, by part (see generatorMemory).
  const flights = useRunsInFlight(docId);
  // The database's category for each part, falling back to the model's. Same rule as the
  // table below: a part's category is a fact about the part, and the row is where a part's
  // facts live.
  const selectorParts = useMemo<SelectorPart[]>(
    () =>
      items.map((p, i) => {
        const itemId = identities[i]?.itemId ?? null;
        const genKey = partGenKey(p, submodelFilter);
        return {
          key: keys[i],
          // THE ID, THEN THE NAME — the shape a made piece's segment already had ("101 –
          // Prepared screw"), now on the parts that came in the box. It is the number every
          // prompt, every chip and every answer calls this part by, and the strip was the
          // one place it was missing: matching a sentence's "07" against a row of names
          // meant counting the segments.
          //
          // shownId, so it is the id a PERSON sees: two digits, zero-led, counted from one
          // (see partsCatalogue). The zero is what says "supplied" beside a piece numbered
          // 101, without anybody having to know where the ranges begin.
          //
          // THE NAME ALONE IN THE MESH PHASE, where nothing is read against a sentence's number
          // and the tiles are models of named things.
          name: view === 'meshes'
            ? identities[i]?.name ?? ''
            : `${shownId(i)} – ${identities[i]?.name ?? ''}`,
          // In the 3D pipeline a part is done when its mesh is switched on in the Parts DB
          // — what the assembly reads. In the video one there is no mesh, and the phase's
          // last word on a part is its photograph.
          // BY WHAT THIS PHASE IS FOR. In the mesh phase a part is done when its
          // reconstruction is switched on in the Parts DB — what the assembly reads. In the
          // parts phase the last word on a part is its photograph, which is what the mesh
          // is made from.
          //
          // NULL UNTIL THE PARTS DB HAS ANSWERED, in the mesh phase: a spinner on the mark
          // rather than every part drawn as having no model (see SelectorPart.done).
          //
          // AND WHILE ITS MODEL IS BEING BUILT, the same spinner: which tile a run is on, said on
          // the tile — the run goes on whichever part is open (see generatorMemory).
          done: view === 'meshes'
            ? itemId == null ? false
              : flights[`part:${itemId}`] || !partModels.loaded ? null : partModels.itemIds.has(itemId)
            : !!genKey && generatedUnder(generated, genKey).length > 0,
        };
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [items, identities, keys, view, partModels, generated, submodelFilter, catalog, flights],
  );

  // IN ID ORDER IN PHASE 2, which is the manual's own order: each segment carries its id, and
  // the number is what a reader arrives with — a sentence says "07", the chips say "07", and a
  // strip sorted by anything else makes them hunt for it. No sort to write there, since a
  // supplied part's id IS its position in this list.
  //
  // AND IN KIND ORDER IN PHASE 5, because the two phases are read with different questions.
  // Phase 2 is walked from a sentence that names a number. Phase 5 is walked by WHAT A THING
  // IS: the furniture is what gets modelled and measured, the hardware is bought in and mostly
  // needs no mesh at all, and a screwdriver never needs one — so the furniture leads, the tools
  // come last, and this manual's allen key stops sitting between the chipboard screw and the
  // wood dowel because that is where its id fell. Ascending within each kind, so the number
  // still finds the part.
  //
  // ONE BLOCK, THOUGH, and the kinds are the ORDER within it rather than three blocks of their
  // own. They had a row apiece, which draws the line between furniture and hardware harder than
  // the strip knows it: the ontology decides that line and it is the thing most likely to be
  // wrong about a part — a folder nobody classified makes a component by default. A break in
  // the rows says this is one of three lists; the order says the strip has been tidied, which
  // is what it has been.
  //
  // THE ONTOLOGY'S ANSWER, except that a part FLAGGED as a tool is a tool whatever folder it
  // sits in — the same test the sub-step chips sort their three lines by (see lineOf), asked of
  // the same two facts, so a part cannot be furniture in one place and a tool in the other.
  // The strip in the mesh phase, sorted by group — and in a subphase, only its own kind.
  const orderedParts = useMemo<SelectorPart[]>(() => {
    if (view !== 'meshes') return selectorParts;
    if (sub === 'product') return [];
    const rankAt = (i: number) => PART_GROUPS.indexOf(partKindOf(items[i], categories[i], ontology));
    return selectorParts
      .map((seg, i) => ({ seg, rank: rankAt(i), at: i }))
      .filter(({ at }) => inSub(at) && !inASet.has(at))
      // BY KIND, THEN BY ID. `at` is the id — a supplied part's position in the list is its
      // number — so the second term keeps each kind in the order Phase 2 reads in.
      .sort((a, b) => a.rank - b.rank || a.at - b.at)
      .map(({ seg }) => seg);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectorParts, view, items, categories, ontology, sub, inASet]);

  // WHICH SEGMENT IS LIT, in the strip's own terms. One expression for it, used by the strip
  // and by the arrow keys, so the two cannot disagree about where the strip is standing.
  const hwModels = view === 'meshes' && sub === 'hardware';
  // A SUBPHASE WHOSE MODELS ARE PICKED IN THE CARD, sets first — not tiles of the strip.
  const picked = hwModels || (view === 'meshes' && sub === 'tools');
  // AND WHICH OF ITS TABS IS OPEN, for the phase's badges — see hardwareTab.
  useEffect(() => {
    if (hwModels) setHardwareTab(docId, hwView === HW_TABLE_KEY ? 'table' : hwView === HW_AXIS_KEY ? 'axis' : 'models');
  }, [docId, hwModels, hwView]);
  const activeKey = hwView ?? (hwModels ? HW_MODELS_KEY : shownSet ? SET_KEY(shownSet.set.key)
    : showAll ? ALL_KEY : showKind ? KIND_KEY(showKind) : keys[open]);

  // WHICH OF THE HARDWARE'S MODELS IS ON SCREEN, centred at the top of its card: each set built
  // together, then the hardware in no set. They were tiles of the strip; the
  // strip has one "3D models" tab now, and this is what it holds.
  // A DATABASE MARK on whatever has a model kept — a set any of whose models is in, a part whose is.
  const keptSets = useKeptSetKeys(docId);
  const keptMark = <Database className="h-3.5 w-3.5 shrink-0 text-blue-600" />;
  // A SET'S NAME — see setName. Only Hardware & covers' sets start with pieces made under Tools.
  const setLabel = (parts: { name: string; at: number }[]) =>
    setName(parts, (p) => p.name, (p) => sub === 'hardware' && toolDriven.has(p.at));
  const modelPicker = picked && !hwView && (
    <CenteredSelect
      look="pill"
      big
      value={shownSet ? SET_KEY(shownSet.set.key) : keys[open]}
      options={[
        ...sets.map(({ set }) => ({
          value: SET_KEY(set.key), label: setLabel(set.parts),
          ...(keptSets.has(set.key) ? { icon: keptMark } : {}),
        })),
        ...items.flatMap((_, i) => (inSub(i) && !inASet.has(i) && !madeWithTool(i)
          ? [{
            value: keys[i], label: selectorParts[i]?.name ?? keys[i],
            ...(selectorParts[i]?.done ? { icon: keptMark } : {}),
          }] : [])),
      ]}
      onChange={setOpenKey}
      ariaLabel={t('notice.hardwareSet.pick')}
    />
  );
  // THE "3D MODELS" TAB opens the model last on screen, or the first.
  const selectTab = (key: string) => {
    if (key !== HW_MODELS_KEY) return setOpenKey(key);
    const last = lastModel.current;
    const known = last && (sets.some(({ set }) => SET_KEY(set.key) === last) || keys.includes(last));
    setOpenKey(known ? last : sets[0] ? SET_KEY(sets[0].set.key) : keys[Math.max(0, firstInSub)]);
  };

  // The "all" segment leads the parts in PHASE 2, and carries the mark every other segment does:
  // done when every part is. It is the one place that phase answers "is this phase finished" in
  // a word, which is otherwise a count of ticks across a dozen segments. The mesh phase has the
  // whole product in that position instead, and no "all" at all — the two are not alternatives,
  // they are what each phase's strip can usefully stand on.
  const segments = useMemo<SelectorPart[]>(
    () => [
      // EVERY PART AT ONCE, and each kind of them at once — see showAll and showKind. ON A ROW
      // OF THEIR OWN, these four and nothing else: ways of looking at the parts, over the parts.
      ...(view === 'meshes'
        ? []
        : [
          {
            key: ALL_KEY,
            name: t('notice.parts.allParts'),
            group: 'views',
            done: orderedParts.length > 0 && orderedParts.every((p) => p.done),
          },
          ...PART_GROUPS.map((kind) => {
            const ofKind = kindCols(kind).map((i) => selectorParts[i]).filter(Boolean);
            return {
              key: KIND_KEY(kind),
              name: t(`notice.parts.kind${kind}`),
              group: 'views',
              done: ofKind.length > 0 && ofKind.every((p) => p.done),
            };
          }),
        ]),
      // HARDWARE & COVERS: THREE TABS — the joints table, the models (one tab, its dropdown in the
      // card: see modelPicker) and the insertion table, last, measured off the models before it.
      ...(hwModels ? [
        { key: HW_TABLE_KEY, name: t('notice.hardwareTable.badge'), group: 'views', noMark: true },
        { key: HW_MODELS_KEY, name: t('notice.hardwareSet.models'), group: 'views', noMark: true },
        { key: HW_AXIS_KEY, name: t('notice.hardwareAxis.title'), group: 'views', noMark: true },
      ] : []),
      ...(picked ? [] : orderedParts),
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [orderedParts, t, view, hwModels, picked, selectorParts, items, categories, ontology],
  );

  // THE ARROW KEYS WALK THE STRIP, in the order the strip DRAWS. Off `segments` and not off
  // the parts list, because those are two different orders: the strip sorts by category rank
  // so the components, the hardware and the tools sit contiguously, and the manual's own
  // order interleaves them — this one puts the allen key eighth, between a screw and a dowel.
  // Walking the list while reading the strip jumped from Chipboard screw to Allen key, past
  // the tile beside it.
  //
  // Not a fifteenth thing to remember: ALL PARTS is where the strip starts, so left from the
  // first part lands on it and left again stops there — see useArrowSteps, which does not wrap
  // and which leaves a typist alone. Half of this phase is editable fields.
  //
  // NOT ON THE MESHES VIEW (Phase 5), where a part is changed by clicking its tile: there the
  // arrows belong to the mesh on screen — up and down its models, left and right its passes.
  // See MeshRunStrip.
  const tabs = useMemo(() => segments.map((seg) => seg.key), [segments]);
  const strip = useArrowSteps<HTMLDivElement>(
    Math.max(0, tabs.indexOf(activeKey)),
    tabs.length,
    (next) => setOpenKey(tabs[next]),
    view !== 'meshes',
  );

  // The button says what pressing it will do, which is now two different things.
  //
  // Before there is a list there is no selection either, so it keeps the phase's own name:
  // "List the supplied parts" is what the first press does, and naming a scope nobody has
  // chosen yet would be answering a question that has not been asked.
  // THE SCOPE OF EVERY BADGE BESIDE THE BUTTON, and of the table under it: the open part, or
  // every part under "All parts".
  const scope = showAll || showKind || !items.length ? undefined : open;
  const busyOrLocked = running || !!locked;
  // EACH BADGE'S STOP, where its run was started on its own: under a "Run all" the chain's Pause
  // is the stop, and halting here would stop it without the chain knowing (see the press's).
  const stop = locked ? undefined : () => stopRuns(docId);
  // THE BADGES BESIDE THE BUTTON, keyed by call, in the order the work happens — split below
  // into the ones this press runs and the ones it does not. See the row under the button.
  //
  // ALL OF THEM FROM THE START, greyed out where they cannot run yet: every one but Parts asks
  // about the parts, so before there is a list only Parts runs. Drawn anyway, so the phase's
  // whole sequence is on screen, and each prompt can be read before the first press.
  const questionsOff = busyOrLocked || !hasList;
  const badge: Record<string, ReactNode> = {
    parts: <PartsListInfo key="parts" docId={docId} file={file} bands={bands} disabled={busyOrLocked} onStop={stop} />,
    schema: (
      <SchemaBoxesInfo
        key="schema" docId={docId} file={file} items={listedItems.current} only={scope}
        disabled={questionsOff} onStop={stop}
      />
    ),
    size: (
      <DimensionsInfo
        key="size"
        docId={docId}
        items={listedItems.current}
        onRun={() => void sizeAlone()}
        running={sizingAlone}
        disabled={questionsOff}
        error={sizeError}
      />
    ),
    crops: (
      <ListingCropsInfo
        key="crops" docId={docId} file={file} items={listedItems.current} only={scope}
        categories={categories} disabled={questionsOff} onStop={stop}
      />
    ),
    // AND THE OTHER HALF OF THE PARTS LIST, next to it: the hardware, which no photograph of
    // an assembled product can show and which the manual draws instead. Its own call, one per
    // page — see hardwareCropsRun.
    //
    // NOT IN THE PRESS. The phase button runs the chain that ends in a material per part, and
    // this answers a question nothing downstream reads yet: it is a badge with a Run, like the
    // size read beside it, and the day something needs it the chain can take it.
    hardware: (
      <HardwareCropsInfo
        key="hardware" docId={docId} file={file} items={listedItems.current}
        categories={categories} disabled={questionsOff} onStop={stop}
      />
    ),
    masks: (
      <MasksInfo key="masks" docId={docId} items={listedItems.current} only={scope} disabled={questionsOff} onStop={stop} />
    ),
    estimate: (
      <EstimateInfo
        key="estimate" docId={docId} file={file} items={listedItems.current} only={scope}
        productName={listing.productName} categories={categories} disabled={questionsOff} onStop={stop}
      />
    ),
  };
  // Each call by the name its own badge carries, for the press's menu.
  const BADGE_NAMES: Record<string, string> = {
    parts: 'notice.parts.badgeList', schema: 'notice.parts.badgeSchema', size: 'notice.parts.badgeSize',
    crops: 'notice.parts.badgeCrops', hardware: 'notice.parts.badgeHardwareCrops',
  };
  const ORDER = ['parts', 'schema', 'size', 'crops', 'hardware', 'masks', 'estimate'];
  // What listAndSize runs: the whole chain on "All parts", the one-part re-read otherwise.
  // A WHOLE CHAIN RUNNING STAYS ONE (`stage` is only set by it): the list landing mid-run opens
  // a part, which is a scope, and the rows and the label were redrawn as a one-part press.
  const wholeChain = scope == null || stage !== null;
  const pressed = new Set(wholeChain
    ? ['parts', 'schema', 'size', 'crops', 'hardware']
    : ['parts']);
  const pressBadges = ORDER.filter((k) => badge[k] && pressed.has(k)).map((k) => badge[k]);
  const otherBadges = ORDER.filter((k) => badge[k] && !pressed.has(k)).map((k) => badge[k]);
  const runLabel = !hasList
    ? t('notice.parts.run')
    : showAll || showKind || stage !== null
      ? t('notice.parts.runAll')
      : t('notice.parts.runOne').replace(
        '{part}',
        identities[open]?.name || items[open]?.nameEn || items[open]?.name
          || t('notice.parts.thisPart'),
      );

  // During a "Run all" pipeline, this phase is "pending" until it runs: an earlier phase
  // is still executing (Phase 1's classification, which this needs) and no parts have been
  // extracted yet. Show only the waiting control — never the previous run's parts or an
  // empty viewer. An error ends the pending state so the failure is shown.
  const pending = !!locked && !busy && !error && items.length === 0;
  if (pending) return <PendingPhase held={held} />;

  return (
    <div className="flex flex-col gap-6">
      {/* Controls, on top: the extract button (and the size read it carries). They are the
          phase's only DOCUMENT-wide action — everything below is about one part. */}
      {view === 'parts' && bands.pages.length > 0 && (
        <PhaseControls>
          {/* THE PRESS AND ITS CALLS' BADGES, ONE ROW. The press was a big button over the
              row, a second way to run what the badges run; it is the row's first badge now,
              and its menu says which of the others it runs. See ChainBadge. */}
          <div className="flex flex-col items-center gap-3">
            {/* STOP ONLY ON A PRESS OF ITS OWN: under a "Run all" it could not stop the run,
                only this phase — the orchestrator's cancel flag is set by the run-all controls
                and nothing else — so the chain went on to the next phase with whatever this
                one had half-finished. Phase 1's Stop and Pause govern the run. */}

            {/* WHAT THIS BUTTON WOULD ASK, AND WHAT IT ANSWERED — one badge per call, each a Run
                and its prompt, reasoning & output, scoped like the button: the open part, or
                every part under "All parts". Only Parts before there is a list: the others are
                questions ABOUT the parts.

                SPLIT BY A "/": on the left the calls this press makes, on the right the ones
                only their own Run makes — and the split moves with the tab. Under "All parts"
                the press lists the parts, re-boxes the hardware's drawings (Schema's pass, on
                the small parts), reads the sizes, finds the crops and asks the materials; on
                one part it re-reads that part and nothing else. Each side keeps the order the
                work happens in. */}
            {/* THREE ROWS: the press; the calls it makes, in the order it makes them; and the
                calls only their own Run makes. */}
            <ChainBadge
              label={runLabel}
              steps={ORDER.filter((k) => badge[k] && pressed.has(k)).map((k) => t(BADGE_NAMES[k]))}
              onRun={() => void listAndSize()}
              // Not under a "Run all": halting there stops every phase without the run-all
              // knowing it was paused — its own Pause does that (see the note above).
              onStop={stop}
              busy={running}
              // And while the size read runs alone: the press would read the sizes again under it.
              disabled={!!locked || sizingAlone}
              // WHICH OF THE FOUR, and how long: the extraction's own page count rides on the
              // stage, that being progress WITHIN it.
              progress={
                <>
                  {stage !== null && `${stage + 1}/${STAGES.length} ${t(STAGE_KEYS[stage])}${progressSuffix} `}
                  ({clock(waited)})
                </>
              }
            />
            <div className="flex flex-wrap items-center justify-center gap-2">{pressBadges}</div>
            {otherBadges.length > 0 && (
              <div className="flex flex-wrap items-center justify-center gap-2">{otherBadges}</div>
            )}
          </div>
        </PhaseControls>
      )}

      {error && <p className="text-center text-sm text-rose-600">{error}</p>}

      {/* Which colour is not chosen here: it is the document's, chosen once for every phase
          (see SubmodelPicker). */}

      {/* Which part. Drawn for a single part too: it is the heading of everything under it,
          and a page whose first line is a table row does not say what it is about.

          PINNED, under the reader's phase cards. This phase is the long one — a table, a
          gallery, a 3D panel and a CAD upload for one part — and the selector is both the
          heading of all of it and the only way to the next part. Scrolled away, changing part
          meant scrolling back up to a bar you could no longer see, and the mark on each
          segment saying what is still to do was off screen for the whole of the work it
          describes.

          The offset is the header's MEASURED height (--notice-header, published by
          NoticeViewer): the cards wrap onto a second row at a narrow width, and a constant
          here would either overlap them or leave a gap under them.

          NOT PINNED. It stays where it sits in the page and scrolls away with everything
          else, matching the phase-nav header above it. Pinned, it followed the reader down
          the page and needed an opaque fill so content would not scroll through it — and
          every fill was wrong in the same way: slate-100/95 was written when the pane behind
          it was slate-100, and a flat page colour is a rectangle with no grid in it. With
          nothing pinned there is nothing to fill, and the page shows through between the
          tiles. */}
      {/* NOT OVER THE WHOLE PRODUCT, where the strip was one tile naming the one thing there
          is: a selector with nothing to select. */}
      {/* NOR OVER THE TOOLS, whose models are picked in the card (see modelPicker). */}
      {(items.length > 0 || view === 'meshes') && !showProduct && !(picked && !hwModels) && (
        <div ref={strip}>
          <PartSelector
            parts={segments}
            activeKey={activeKey}
            onSelect={selectTab}
            // THE SAME MARK IN BOTH PHASES, and it is the database's. A part is done here
            // when the Parts DB holds its photograph, exactly as it is done in the mesh
            // phase when the Parts DB holds its model — one question asked twice, drawn as
            // a sparkle in one and a database in the other. The sparkle also said the wrong
            // thing: "a model made this picture" is true of every image in the app, and is
            // not what the mark is answering.
            icon={Database}
            doneLabel={t(view === 'meshes' ? 'notice.3d.inDb' : 'notice.parts.drawn')}
            todoLabel={t(view === 'meshes' ? 'notice.3d.addToDb' : 'notice.parts.notDrawn')}
          />
        </div>
      )}

      {/* The open part's table: what it is, what the manual prints about it, its drawing,
          and the pictures generated from that drawing. RealImagesStep owns the generator;
          the table is one column of it. */}
      {/* The parts phase draws what the manual prints; the mesh phase draws the two rows a
          reconstruction is made from — the context photographs and the render. One table,
          told which of its rows this phase is about. */}

      {/* And the same part as a mesh, under everything it was reconstructed from. The 3D
          pipeline only — the video one has nothing to put here.

          Behind a rule, as the phase's other blocks are: the table answers what the part
          IS and what it looks like, and what follows is one thing made out of those
          answers. Without it the toggle floated between two bordered panels, belonging to
          whichever you read it next to. */}
      {/* WHY THERE IS NOTHING HERE, when there is nothing here. The mesh phase draws ONE part's
          reconstruction, and an unread manual has no part to draw — an empty phase under a
          selector reads as a phase that failed rather than as one waiting for the list.
          "All parts" used to land here too, and does not any more: it is not a segment of this
          strip (see showAll), which is a better answer than a tile that shows this line. */}
      {view === 'meshes' && !showProduct && items.length === 0 && (
        <p className="text-center text-sm text-slate-400">
          {t('notice.models.needsParts')}
        </p>
      )}

      {/* THE WHOLE PRODUCT, in the place a part's mesh is drawn and behind the same rule:
          the segment above chose it, and what follows is one thing made out of pictures —
          which is exactly what the part panel below is, on a different subject. */}
      {showProduct && (
        <>
          <ProductModelStep docId={docId} locked={locked} />
        </>
      )}

      {/* A SUBPHASE WITH NONE OF ITS KIND: a manual with no hardware listed, say. */}
      {/* HARDWARE & COVERS' TABLES, under their tabs. */}
      {hwView === HW_TABLE_KEY && <HardwareTable docId={docId} />}
      {hwView === HW_AXIS_KEY && <HardwareAxisTable docId={docId} />}
      {models && (genSet ?? genFirstSet) && (
        <HardwareSetStep
          docId={docId} set={(genSet ?? genFirstSet)!.set} interaction={(genSet ?? genFirstSet)!.one}
          items={items} locked={locked} onKept={() => invalidatePartModels()} badgeOnly
        />
      )}
      {models && genPart >= 0 && (
        <ThreeDStep
          docId={docId} file={file} locked={locked} part={items[genPart]} partAt={genPart}
          itemId={identities[genPart]?.itemId ?? null} partName={identities[genPart]?.name ?? ''} badgeOnly
        />
      )}
      {view === 'meshes' && items.length > 0 && !showProduct && !hwView && !shownSet && firstInSub < 0 && (
        <p className="text-center text-sm text-slate-400">
          {t(sub === 'hardware' ? 'notice.models.noHardware'
            : sub === 'tools' ? 'notice.models.noTools' : 'notice.models.noComponents')}
        </p>
      )}
      {/* A SET, BUILT TOGETHER — the segment's own card, where the parts of it get their
          models. See HardwareSetStep. */}
      {view === 'meshes' && models && shownSet && (
        <HardwareSetStep
          docId={docId}
          corner={modelPicker}
          set={shownSet.set}
          interaction={shownSet.one}
          file={file}
          items={items}
          locked={locked}
          onKept={() => invalidatePartModels()}
        />
      )}
      {view === 'meshes' && models && items.length > 0 && !showProduct && !hwView && !shownSet && firstInSub >= 0 && (
        <>
          <ThreeDStep
            docId={docId}
            file={file}
            locked={locked}
            part={items[open]}
            partAt={open}
            itemId={identities[open]?.itemId ?? null}
            partName={identities[open]?.name ?? ''}
            corner={modelPicker || undefined}
          />
        </>
      )}
      {/* THE PARTS TABLE, in the parts phase only. The mesh phase used to mount it as the
          image GENERATOR (the context photographs and a model's invented photograph of the
          part, beneath the mesh), and that generator is gone from every part. */}
      {!showProduct && view !== 'meshes' && (
      <RealImagesStep
        file={file}
        docId={docId}
        locked={locked}
        sizing={sizing}
        coming={stage === null ? undefined : { crops: owes('crops') }}
        views={models}
        imageRows="without"
        // Undefined draws every part as its own column, which is the whole of what "all
        // parts" is: the table needed no new mode, only permission to do what it does.
        only={scope}
        // AND ONE KIND'S COLUMNS ALONE, under its tab.
        onlyCols={showKind ? kindCols(showKind) : undefined}
      />
      )}

      {/* OUT TO A REAL BLENDER, at the foot of the phase and below the tables.

          Here and not in ThreeDStep, where it used to sit: it is not one part's, it is the
          notice's — the add-on is set up per account and the .blend files it lists are the
          manual's — so it hung mid-page above the picture table, between two things it
          had nothing to do with, and it appeared and vanished with whichever part was
          open. The whole mesh phase now, whatever the selector is pointed at.

          Not in the demo: the round trip is an add-on installed in somebody's own Blender,
          talking to this notice over a bearer token. There is nothing here for a visitor
          to do and the dialog's whole text is instructions for doing it. */}
      {/* AND THE HOLES DRILLED IN THE PART ON SCREEN, under its mesh — see HolesTable. Under
          and not over: the mesh is what somebody came to look at, and a table of a dozen bores
          above it pushed it off the screen. */}
      {view === 'meshes' && models && sub === 'components' && !showProduct && !shownSet
        && firstInSub >= 0 && <HolesTable docId={docId} only={open} locked={locked} />}

    </div>
  );
}
